import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';

import {
  IGetWorkspacesQueryParams,
  IGetWorkspacesResponse,
  IWorkspace,
} from '../../../../../../shared/interfaces/workspace.interface';
import { IModal } from '../../../../components/modals/modal.interface';
import { ModalContainerComponent } from '../../../../components/modals/modal-container.component';
import { ITableConfig, ITableRow } from '../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { DEFAULT_ENTRY_LIMITS } from '../../../../constants/form.constants';
import { ModalService } from '../../../../services/modal.service';
import { WorkspaceService } from '../../../../services/workspace.service';
import { ApiQueryOption } from '../../../../../../shared/constants/api';
import { $workspace } from '../../../../../../shared/constants/workspace';
import { NgbTableUtilities } from '../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { $tenant } from '../../../../../../shared/constants/tenant';
import { TranslateService } from '../../../../translate/translate.service';
import { IMapWorkspaceIdToName } from '../../../../../../shared/interfaces/admin.interface';
import { $sortingOrder } from '../../../../../../shared/constants/sort';

interface IWorkspaceSearchFormValues {
  [$workspace.name]: string;
}

@Component({
  templateUrl: './migrate-workspace-modal.component.html',
  styleUrls: ['./migrate-workspace-modal.component.scss'],
  standalone: false,
})
export class MigrateWorkspaceModalComponent implements IModal, OnInit, OnDestroy {
  private subscription: Subscription;
  private selectedWorkspacesMap: IMapWorkspaceIdToName;

  @ViewChild(ModalContainerComponent, { static: true })
  public container: ModalContainerComponent;

  @Output()
  public onSelectWorkspace: EventEmitter<Map<string, string>>;

  public tenantId$: Observable<string>;
  public form: UntypedFormGroup;
  public tableConfig: ITableConfig;

  public pageSizes: readonly number[];

  public $workspace: typeof $workspace;
  public searchTerm$: BehaviorSubject<string>;
  public page$: BehaviorSubject<number>;
  public pageSize$: BehaviorSubject<number>;

  public totalRows$: Observable<number>;
  public tableData$: Observable<ITableRow<IWorkspace>[]>;
  public selectedWorkspacesCount: number;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService,
    private workspaceService: WorkspaceService,
    private translateService: TranslateService,
  ) {
    this.subscription = new Subscription();
    this.$workspace = $workspace;
    this.selectedWorkspacesMap = new Map<string, string>();
    this.onSelectWorkspace = new EventEmitter<Map<string, string>>();
    this.createSearchForm();

    this.pageSizes = DEFAULT_ENTRY_LIMITS;
    this.searchTerm$ = new BehaviorSubject<string>('');
    this.page$ = new BehaviorSubject<number>(1);
    this.pageSize$ = new BehaviorSubject<number>(this.pageSizes[this.pageSizes.length - 1]);
    this.tableConfig = this.createTableConfig();
    this.selectedWorkspacesCount = 0;
  }

  private createSearchForm(): void {
    const formConfig: Record<string, UntypedFormControl> = {
      [$workspace.name]: new UntypedFormControl(''),
    };
    this.form = this.formBuilder.group(formConfig);
  }

  private createTableConfig(): ITableConfig {
    return {
      columns: {
        checkbox: {
          translationKey: '',
          styles: {
            maxWidth: '5rem',
            width: '5rem',
          },
        },
        [$workspace.name]: {
          translationKey: 'label_workspace_name',
          styles: {
            width: '50%',
          },
        },
        [$workspace.tenant]: {
          translationKey: 'current_associated_tenant',
          styles: {
            width: '50%',
          },
        },
      },
      caption: this.translateService.instant('available_workspaces'),
    };
  }

  private toTableData(workspaces: IWorkspace[], tenantId: string): ITableRow<IWorkspace>[] {
    const toTableRow = (workspace: IWorkspace): ITableRow => {
      const isDisabled: boolean = workspace[$workspace.tenantId] === tenantId;
      const tableRow: ITableRow = {
        data: {
          checkbox: NgbTableUtilities.checkboxCell({
            checked: this.selectedWorkspacesMap.has(workspace[$workspace._id]),
            disabled: isDisabled,
            attributes: {
              'aria-label': this.translateService.instant('select_label', workspace[$workspace.name]),
            },
            styles: {
              marginTop: '5px',
            },
            listeners: {
              click: ($event: MouseEvent): void => {
                $event.stopPropagation();
                const id: string = workspace[$workspace._id];
                if (this.selectedWorkspacesMap.has(id)) {
                  this.selectedWorkspacesMap.delete(id);
                  tableRow.styles = {};
                  tableRow.data[$workspace.name] = NgbTableUtilities.textCell({
                    text: workspace[$workspace.name],
                  });
                } else {
                  this.selectedWorkspacesMap.set(id, workspace[$workspace.name]);
                  tableRow.styles = { backgroundColor: '#E9ECEF' };
                  tableRow.data[$workspace.name] = NgbTableUtilities.textCell({
                    text: workspace[$workspace.name],
                    styles: { fontWeight: 'bold' },
                  });
                }
                this.selectedWorkspacesCount = this.selectedWorkspacesMap.size;
              },
            },
          }),
          [$workspace.name]: NgbTableUtilities.textCell({
            text: workspace[$workspace.name],
            classes: isDisabled ? ['disabled-text'] : undefined,
          }),
          [$workspace.tenant]: NgbTableUtilities.textCell({
            text: workspace[$workspace.tenant]?.[$tenant.name] ?? '',
            classes: isDisabled ? ['disabled-text'] : undefined,
          }),
        },
        originalData: workspace,
      };
      return tableRow;
    };
    return workspaces.map(toTableRow);
  }

  public ngOnInit(): void {
    this.subscription.add(
      this.form.valueChanges.subscribe((formValues: IWorkspaceSearchFormValues) => {
        this.searchTerm$.next(formValues[$workspace.name]);
        this.page$.next(1);
      }),
    );

    const workspaces$: Observable<IGetWorkspacesResponse> = combineLatest([this.searchTerm$, this.page$, this.pageSize$]).pipe(
      switchMap(([searchTerm, page, pageSize]: [string, number, number]) => {
        const queryOptions: IGetWorkspacesQueryParams = {
          [ApiQueryOption.term]: searchTerm,
          [ApiQueryOption.page]: page,
          [ApiQueryOption.limit]: pageSize,
          [ApiQueryOption.sortByName]: $sortingOrder.asc,
          [ApiQueryOption.tenantlessOnly]: false,
        };
        return this.workspaceService.getAllWorkspaces(queryOptions);
      }),
    );

    this.totalRows$ = workspaces$.pipe(
      map((data: IGetWorkspacesResponse): number => {
        return data._total;
      }),
    );

    this.tableData$ = workspaces$.pipe(
      withLatestFrom(this.tenantId$),
      map(([data, tenantId]: [IGetWorkspacesResponse, string]) => this.toTableData(data.workspaces, tenantId)),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onPageChange(page: number): void {
    this.page$.next(page);
  }

  public onPageSizeChange(pageSize: number): void {
    this.pageSize$.next(pageSize);
  }

  public onClose(): void {
    this.modalService.closeModal();
  }

  public migrateWorkspace(): void {
    this.onClose();
    this.onSelectWorkspace.next(this.selectedWorkspacesMap);
  }
}
